// =============================================================================
// USAGE BUDGET LEDGER — the per-user, per-period running usage counter.
//
// docs/design/cost-governance/per-user-spend-caps-2026-07-07.md §3.2b. Neither
// ai_call_log nor elevenlabs_call_log can structurally answer "how much has
// THIS user spent THIS period" — both are append-only logs, never
// summed-and-gated. This table is the accumulate side of accumulate-and-deny
// (the same shape hunterBudgetAllows reads off a CACHE_KV counter for a
// platform-wide Hunter budget; this is the per-USER analogue, Neon-backed so
// it survives a KV eviction and is queryable directly).
//
// One row per (user_id, period_key) — period_key is clock-derived (e.g.
// '2026-07-07' for a daily period), never a wall-clock read. Written after
// each call (or in the same transaction as the turn); read at the two gate
// seams (session-mint, per-turn) before the next call.
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { index, integer, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const usageBudgetLedger = pgTable(
  'usage_budget_ledger',
  {
    id: id(),
    // Migration debt noted (per-user-spend-caps brief §3.2b): matches the
    // existing text-keyed user_id pattern the rest of this table's siblings
    // (elevenlabs_call_log, ai_call_log) already carry; not a new debt.
    userId: text('user_id').notNull(),
    // e.g. '2026-07-07' (daily) or '2026-07' (monthly) — clock-derived by the
    // caller (usageBudget.ts), never Date.now() directly.
    periodKey: varchar('period_key', { length: 16 }).notNull(),
    // Cumulative ElevenLabs conversation milliseconds this period.
    voiceMinutesMs: integer('voice_minutes_ms').notNull().default(0),
    // Cumulative Claude cost (micro-USD) this period, from ai_call_log.
    llmCostMicro: integer('llm_cost_micro').notNull().default(0),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('usage_budget_ledger_user_idx').on(t.userId),
    userPeriodUq: uniqueIndex('usage_budget_ledger_user_period_uq').on(t.userId, t.periodKey),
  }),
);
